import React, {FC, useState, useEffect} from 'react';
import {Row, Col, Card, Typography, Button, message, Spin, Modal, Form, Input, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {getCookie} from 'typescript-cookie';
import stripeService from '../../services/stripeService';
import authService from '../../services/authService';
import {formatString, formatLabel, useIsMobile, detectPlatform} from '../../utils/helpers';
import {SocialMediaName, UserObjectApi} from '../../utils/types';
import * as authActions from '../../store/modules/auth/actions';
import {useDispatch} from 'react-redux';
import {validateUsername} from '../../utils/validators';
import RichTextViewer from "../../components/molecules/Forms/RichText/viewer";
import RichText from "../../components/molecules/Forms/RichText/component";
import DOMPurify from "dompurify";
import {useNavigate} from 'react-router-dom';

const {Text} = Typography;

const roleGuideRoutes: Record<string, string> = {
    advertiser: '/advertiser-guide'
};

const ProfilePage: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [user, setUserProfile] = useState<UserObjectApi | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const role = getCookie('roleType') || '';
    const canManageSocialProfile = ['promoter', 'partner'].includes(role);
    const isMobile = useIsMobile();

    const [editModalVisible, setEditModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);

    const [editForm] = Form.useForm();
    const [passwordForm] = Form.useForm();

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            try {
                const result = await authService.getProfile();
                setUserProfile(result.data as UserObjectApi);
                dispatch(authActions.loginSuccess(true, result.data));
            } catch (err: any) {
                console.error(err);
                setError('Failed to load profile.');
                message.error('Could not load profile. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [dispatch]);

    const handleOnboard = async () => {
        if (!user) return;

        try {
            setLoading(true);
            const link = await stripeService.onboardAccount();
            if (typeof link === 'string' && link.startsWith('http')) {
                window.location.href = link;
            } else {
                message.error('Could not get onboarding link. Please try again.');
            }
        } catch (err: any) {
            message.error(err.message || 'Error starting onboarding.');
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = () => {
        if (!user) return;

        editForm.setFieldsValue({
            username: user.username,
            ...(canManageSocialProfile
                ? {
                    bio: user.socials?.bio,
                    numberOfFollowers: user.socials?.numberOfFollowers,
                    links: user.socials?.links || [],
                }
                : {}),
            ...(user.userType === 'individual'
                    ? {firstName: user.profile.firstName, lastName: user.profile.lastName, phone: user.profile.phone}
                    : {
                        companyName: user.businessInfo.companyName,
                        companyWebsite: user.businessInfo.companyWebsite,
                        vatNumber: user.businessInfo.vatNumber,
                        companyAddress: user.businessInfo.companyAddress,
                        phone: user.businessInfo.phone
                    }
            )
        });
        setEditModalVisible(true);
    };

    const handleProfileUpdate = async (vals: any) => {
        try {
            let safeBio = '';
            let parsedLinks: { url: string; type: SocialMediaName }[] = [];

            if (canManageSocialProfile) {
                safeBio = DOMPurify.sanitize(
                    typeof vals.bio === 'string' ? vals.bio : user?.socials?.bio || '',
                    {
                        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li'],
                        ALLOWED_ATTR: [],
                    }
                );

                const isEmptyBio =
                    !safeBio ||
                    safeBio.replace(/<[^>]*>/g, '').trim() === '';

                if (isEmptyBio) {
                    safeBio = '';
                }

                parsedLinks = (vals.links || [])
                    .filter((l: any) => l?.url)
                    .map((l: any) => {
                        const type = detectPlatform(l.url);

                        if (!type) {
                            throw new Error(`Invalid or unsupported link: ${l.url}`);
                        }

                        return {
                            url: l.url,
                            type
                        };
                    });
            }
            
            await authService.updateProfile({
                username: vals.username.toLowerCase().trim(),
                userType: user!.userType,
                profile: user!.userType === 'individual'
                    ? {firstName: vals.firstName, lastName: vals.lastName, phone: vals.phone}
                    : undefined,
                businessInfo: user!.userType === 'business'
                    ? {
                        companyName: vals.companyName,
                        companyWebsite: vals.companyWebsite,
                        vatNumber: vals.vatNumber,
                        companyAddress: vals.companyAddress,
                        phone: vals.phone
                    }
                    : undefined,
                ...(canManageSocialProfile
                    ? {
                        bio: safeBio,
                        numberOfFollowers:
                            vals.numberOfFollowers !== undefined && vals.numberOfFollowers !== ''
                                ? Number(vals.numberOfFollowers)
                                : undefined,
                        links: parsedLinks
                    }
                    : {})
            });
            message.success('Profile updated!');
            setEditModalVisible(false);

            const profile = await authService.getProfile();
            setUserProfile(profile.data);
            dispatch(authActions.loginSuccess(true, profile.data));
        } catch (err: any) {
            message.error(err.response?.data?.message || 'Failed to update profile.');
        }
    };

    const openPasswordModal = () => {
        passwordForm.resetFields();
        setPasswordModalVisible(true);
    };

    const handlePasswordUpdate = async (vals: any) => {
        try {
            await authService.updatePassword({
                oldPassword: vals.oldPassword,
                newPassword: vals.newPassword
            });
            message.success('Password changed!');
            setPasswordModalVisible(false);
        } catch (err: any) {
            message.error(err.response?.data?.message || 'Failed to update password.');
        }
    };

    if (loading) return <Spin style={{margin: 20}}/>;

    if (error || !user) return <Text type="danger">{error || 'No user profile data.'}</Text>;

    const hasProfileData = user.userType === 'individual' && Object.keys(user.profile).length > 0;
    const hasBusinessData = user.userType === 'business' && Object.keys(user.businessInfo).length > 0;

    const usernameRules = [
        {
            validator: (_: any, value: string) => {
                const result = validateUsername(value || '');
                return result.valid
                    ? Promise.resolve()
                    : Promise.reject(new Error(result.errors[0]));
            }
        }
    ];

    const containerStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: 1100,
        margin: '0 auto',
        padding: isMobile ? 12 : 20
    };

    const wrapValueStyle: React.CSSProperties = {
        wordBreak: 'break-word',
        overflowWrap: 'anywhere'
    };

    const userRoleEntries = Array.isArray((user as any).roles)
        ? (user as any).roles
        : (user as any).roles
            ? [(user as any).roles]
            : [];
    const activeRoleInfo = userRoleEntries.find((r: any) => r.roleType === role);
    const visibleRoles = activeRoleInfo ? [activeRoleInfo] : [{roleType: role}];

    return (
        <div style={containerStyle}>
            <label className="title">My profile</label>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card>
                        <div style={wrapValueStyle}>
                            <Text strong>Username:</Text> {user.username}
                            <br/>
                            <Text strong>Email:</Text> {user.email}
                            <br/>
                            <div style={{display: 'flex', alignItems: 'center', gap: 4, lineHeight: '24px'}}>
                                <Text strong>Role:</Text>
                                <div style={{display: 'grid', gap: 6}}>
                                    {visibleRoles.map((roleItem: any) => {
                                        const roleType = roleItem.roleType || role;
                                        const guidePath = roleGuideRoutes[roleType];

                                        return (
                                            <span
                                                key={roleType}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    lineHeight: '24px'
                                                }}
                                            >
                                                <span>{formatString(roleType)}</span>
                                                {guidePath && (
                                                    <Tooltip
                                                        title="Clicking this will open the guide page where you can learn more about this role."
                                                    >
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            icon={<InfoCircleOutlined/>}
                                                            onClick={() => navigate(guidePath)}
                                                            aria-label={`Open ${formatString(roleType)} guide`}
                                                            style={{
                                                                height: 18,
                                                                width: 18,
                                                                minWidth: 18,
                                                                padding: 0,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#1677ff',
                                                                verticalAlign: 'middle'
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                            <br/>
                            <Text strong>User type:</Text> {formatString(user.userType)}
                            <br/>
                            <Text strong>Can withdraw:</Text> {user.canWithdraw ? 'Yes' : 'No'}
                            {user.recruitedBy && (
                                <>
                                    <br/>
                                    <Text strong>Recruited by:</Text> {user.recruitedBy.username || '—'}
                                </>
                            )}
                        </div>
                    </Card>
                </Col>

                {user.userType === 'individual' && (
                    <Col span={24}>
                        <Card title="Personal details">
                            {hasProfileData ? (
                                <div style={wrapValueStyle}>
                                    <Text strong>First name:</Text> {user.profile.firstName}
                                    <br/>
                                    <Text strong>Last name:</Text> {user.profile.lastName}
                                    <br/>
                                    <Text strong>Phone:</Text> {user.profile.phone}
                                </div>
                            ) : (
                                <Text type="secondary">No personal details available.</Text>
                            )}
                        </Card>
                    </Col>
                )}

                {user.userType === 'business' && (
                    <Col span={24}>
                        <Card title="Business details">
                            {hasBusinessData ? (
                                <div style={wrapValueStyle}>
                                    <Text strong>Company name:</Text> {user.businessInfo.companyName}
                                    <br/>
                                    <Text strong>Website:</Text>{' '}
                                    <a href={user.businessInfo.companyWebsite} target="_blank"
                                       rel="noopener noreferrer">
                                        {user.businessInfo.companyWebsite}
                                    </a>
                                    <br/>
                                    <Text strong>VAT Number:</Text> {user.businessInfo.vatNumber}
                                    <br/>
                                    <Text strong>Address:</Text> {user.businessInfo.companyAddress}
                                    <br/>
                                    <Text strong>Phone:</Text> {user.businessInfo.phone}
                                </div>
                            ) : (
                                <Text type="secondary">No business information available.</Text>
                            )}
                        </Card>
                    </Col>
                )}

                {canManageSocialProfile && (
                    <Col span={24}>
                        <Card title="Social Profile">
                            {user.socials && (user.socials.bio || user.socials.links?.length > 0) ? (
                                <div style={wrapValueStyle}>

                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong>About:</Text>
                                        <div style={{ marginTop: 6 }}>
                                            <RichTextViewer key={user.socials.bio} html={user.socials.bio || ''} />
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong>Followers:</Text>{' '}
                                        {user.socials.numberOfFollowers || 0}
                                    </div>

                                    <div>
                                        <Text strong>Social Links:</Text>

                                        <ul
                                            style={{
                                                marginTop: 8,
                                                paddingLeft: 20,
                                                listStyleType: 'disc'
                                            }}
                                        >
                                            {(user.socials.links || []).map((link: any, index: number) => (
                                                <li key={index}>
                                                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                                                        {link.url}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                </div>
                            ) : (
                                <Text type="secondary">No social profile information available.</Text>
                            )}
                        </Card>
                    </Col>
                )}
                
                <Col span={24}>
                    <Card title="Settings">
                        {Object.entries(user.settings?.notifications).length === 0 ? (
                            <Text type="secondary">No settings configured.</Text>
                        ) : (
                            Object.entries(user.settings).map(([key, val]) => (
                                <div key={key} style={{marginBottom: 8}}>
                                    <Text strong>{formatLabel(key)}:</Text> {val}
                                </div>
                            ))
                        )}
                    </Card>
                </Col>
            </Row>

            <div
                style={{
                    marginTop: 20,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    flexWrap: "wrap",
                }}
            >
                <Button block={isMobile} onClick={openEditModal}>
                    Edit Profile
                </Button>

                <Button block={isMobile} onClick={openPasswordModal}>
                    Change Password
                </Button>

                {!user.canWithdraw && (
                    <Button block={isMobile} type="primary" onClick={handleOnboard}>
                        Get verified
                    </Button>
                )}
            </div>


            {/* ——— Edit Profile Modal ——— */}
            <Modal
                title="Edit Profile"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                footer={null}
                destroyOnClose
                width={isMobile ? '92vw' : 520}
                centered
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleProfileUpdate}
                    initialValues={{}}
                >
                    <Form.Item
                        label="Username"
                        name="username"
                        rules={usernameRules}
                    >
                        <Input
                            onChange={e => {
                                editForm.setFieldsValue({
                                    username: e.target.value.toLowerCase()
                                });
                            }}
                        />
                    </Form.Item>

                    {user.userType === 'individual' ? (
                        <>
                            <Form.Item
                                label="First name"
                                name="firstName"
                                rules={[{required: true, message: 'First name is required'}]}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item
                                label="Last name"
                                name="lastName"
                                rules={[{required: true, message: 'Last name is required'}]}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item label="Phone" name="phone">
                                <Input/>
                            </Form.Item>
                        </>
                    ) : (
                        <>
                            <Form.Item
                                label="Company name"
                                name="companyName"
                                rules={[{required: true, message: 'Company name is required'}]}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item label="Website" name="companyWebsite">
                                <Input/>
                            </Form.Item>
                            <Form.Item label="VAT Number" name="vatNumber">
                                <Input/>
                            </Form.Item>
                            <Form.Item label="Address" name="companyAddress">
                                <Input/>
                            </Form.Item>
                            <Form.Item label="Phone" name="phone">
                                <Input/>
                            </Form.Item>
                        </>
                    )}

                    {canManageSocialProfile && (
                        <>
                            <Form.Item
                                label="About"
                                name="bio"
                                valuePropName="value"
                                getValueFromEvent={(value) => value}
                            >
                                <RichText placeholder="Tell something about yourself..." />
                            </Form.Item>

                            <Form.Item label="Number of followers" name="numberOfFollowers">
                                <Input type="number" />
                            </Form.Item>

                            <Form.Item
                                label="Social links"
                                style={{ marginBottom: 16 }}
                        >
                                <Form.List
                                    name="links"
                                >
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map(({ key, name }) => (
                                                <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                    <Form.Item
                                                        name={[name, 'url']}
                                                        style={{ flex: 1, marginBottom: 0 }}
                                                        rules={[
                                                            {
                                                                validator: (_, value) => {
                                                                    if (!value) return Promise.resolve();

                                                                    const type = detectPlatform(value);
                                                                    if (!type) {
                                                                        return Promise.reject(
                                                                            new Error('Only Facebook, Instagram, TikTok, X, LinkedIn, YouTube, Twitch links are allowed')
                                                                        );
                                                                    }

                                                                    return Promise.resolve();
                                                                }
                                                            }
                                                        ]}
                                                    >
                                                        <Input placeholder="https://..." />
                                                    </Form.Item>

                                                    <Button danger onClick={() => remove(name)}>X</Button>
                                                </div>
                                            ))}

                                            <Button type="dashed" onClick={() => add()} block>
                                                Add social link
                                            </Button>
                                        </>
                                    )}
                                </Form.List>
                            </Form.Item>
                        </>
                    )}

                    <Form.Item style={{marginBottom: 0}}>
                        <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap'}}>
                            <Button onClick={() => setEditModalVisible(false)} style={{marginRight: isMobile ? 0 : 8}}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Save changes
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            {/* ——— Change Password Modal ——— */}
            <Modal
                title="Change Password"
                open={passwordModalVisible}
                onCancel={() => setPasswordModalVisible(false)}
                footer={null}
                destroyOnClose
                width={isMobile ? '92vw' : 520}
                centered
            >
                <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={handlePasswordUpdate}
                    initialValues={{oldPassword: '', newPassword: '', confirmPassword: ''}}
                >
                    <Form.Item
                        label="Current password"
                        name="oldPassword"
                        rules={[{required: true, message: 'Please enter your current password'}]}
                    >
                        <Input.Password/>
                    </Form.Item>
                    <Form.Item
                        label="New password"
                        name="newPassword"
                        rules={[{required: true, message: 'Please enter a new password'}]}
                    >
                        <Input.Password/>
                    </Form.Item>
                    <Form.Item
                        label="Confirm new password"
                        name="confirmPassword"
                        dependencies={['newPassword']}
                        rules={[
                            {required: true, message: 'Please confirm your new password'},
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject('The two passwords do not match');
                                },
                            }),
                        ]}
                    >
                        <Input.Password/>
                    </Form.Item>
                    <Form.Item style={{marginBottom: 0}}>
                        <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap'}}>
                            <Button onClick={() => setPasswordModalVisible(false)}
                                    style={{marginRight: isMobile ? 0 : 8}}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Change password
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

        </div>
    );
};

export default ProfilePage;
